/**
 * Turning questionnaire progress into the answer text the server receives.
 *
 * These are pure and live here rather than inside the 7k-line panel because the
 * JOIN KEY is a cross-process contract: `cleanQuestionPrompt` must normalize a
 * prompt byte-identically to the server's `normalizeAnswerPrompt`
 * (mcp-chat-server `pipeline/clarification-questions.ts`), which maps answers
 * back to questions BY PROMPT rather than by position. A drift here silently
 * binds the wrong answer to the wrong question.
 */
import type { ParsedQuestionBlock, ParsedQuestionnaire, QuestionnaireProgress } from "./mcp-chat-types";
import { isOtherSelection } from "./mcp-chat-utils";

/** Normalize a question prompt into the compact label used in submitted answers. */
export function cleanQuestionPrompt(prompt: string, index: number): string {
  return (
    prompt
      .replace(/\r?\n+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/[:?]\s*$/, "")
      .trim() || `Question ${index + 1}`
  );
}

export function formatQuestionnaireAnswers(
  questionnaire: ParsedQuestionnaire,
  progress: QuestionnaireProgress
): string | null {
  const lines: string[] = [];
  for (let i = 0; i < questionnaire.questions.length; i++) {
    const q = questionnaire.questions[i];
    const prompt = cleanQuestionPrompt(q.prompt, i);

    if (q.multiSelect) {
      const multiValues = progress.multiSelectedValues?.[i] || [];
      if (multiValues.length === 0) return null;
      lines.push(`${prompt}: ${multiValues.join(", ")}`);
    } else {
      const selected = progress.selectedValues[i];
      if (!selected) return null;
      const resolvedValue = isOtherSelection(q, selected) ? (progress.customValues[i] || "").trim() : selected;
      if (!resolvedValue) return null;
      lines.push(`${prompt}: ${resolvedValue}`);
    }
  }
  return lines.join("\n");
}

/**
 * Format only the questions the user actually answered, skipping the rest.
 *
 * The strict `formatQuestionnaireAnswers` above returns null on any gap, which is
 * right for the markdown questionnaire path that has no way to hand a question
 * back. This sibling is for the delegate buttons: an omitted line is already the
 * wire's definition of unanswered — the server maps answers to questions BY
 * PROMPT and reports the missing ones — so a skipped question needs no marker of
 * its own. `cleanQuestionPrompt` is shared so that join key stays byte-identical
 * to the server's `normalizeAnswerPrompt`.
 *
 * Returns "" when nothing was answered, which the server reads as a bare delegate.
 */
export function formatAnsweredQuestionLines(
  questionnaire: ParsedQuestionnaire,
  progress: QuestionnaireProgress
): string {
  const lines: string[] = [];
  for (let i = 0; i < questionnaire.questions.length; i++) {
    const q: ParsedQuestionBlock = questionnaire.questions[i];
    const prompt: string = cleanQuestionPrompt(q.prompt, i);

    if (q.multiSelect) {
      const multiValues: string[] = progress.multiSelectedValues?.[i] || [];
      if (multiValues.length === 0) continue;
      lines.push(`${prompt}: ${multiValues.join(", ")}`);
      continue;
    }
    const selected: string = progress.selectedValues[i] || "";
    if (!selected) continue;
    const resolvedValue: string = isOtherSelection(q, selected) ? (progress.customValues[i] || "").trim() : selected;
    if (!resolvedValue) continue;
    lines.push(`${prompt}: ${resolvedValue}`);
  }
  return lines.join("\n");
}
